import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { ArrowLeft, Save, User, Briefcase, IndianRupee, Building2, FileUp, Trash2, Download, Loader2, Upload, KeyRound, ShieldCheck, ShieldAlert, CheckCircle2, Circle, Eye, EyeOff, Wand2, Mail, UserCheck, UserX, MapPin, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirmation } from '../components/ConfirmationProvider';
import { useAuth } from '../context/AuthContext';
import { PersonAvatar } from '../components/PersonAvatar';
import { ProfilePictureUploader } from '../components/ProfilePictureUploader';
import { PasswordStrengthHint } from '../components/PasswordStrengthHint';
import { checkPasswordStrength, PASSWORD_POLICY_MESSAGE } from '../utils/passwordStrength';
import { SalaryCertificateDialog } from '../components/hr/SalaryCertificateDialog';
import { SalaryCertificateHistory } from '../components/hr/SalaryCertificateHistory';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const formatCurrency = (a) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(a || 0);
const API_URL = process.env.REACT_APP_BACKEND_URL;

const docTypes = [
  { value: 'id_proof', label: 'ID Proof (Aadhaar/PAN)' },
  { value: 'address_proof', label: 'Address Proof' },
  { value: 'offer_letter', label: 'Offer Letter' },
  { value: 'contract', label: 'Employment Contract' },
  { value: 'certificate', label: 'Certificate / Degree' },
  { value: 'other', label: 'Other' },
];

function generateStrongPassword() {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const digits = '23456789';
  const symbols = '@#$%&*!?';
  const all = upper + lower + digits + symbols;
  let pw = '';
  pw += upper[Math.floor(Math.random() * upper.length)];
  pw += lower[Math.floor(Math.random() * lower.length)];
  pw += digits[Math.floor(Math.random() * digits.length)];
  pw += symbols[Math.floor(Math.random() * symbols.length)];
  for (let i = 4; i < 14; i++) pw += all[Math.floor(Math.random() * all.length)];
  return pw.split('').sort(() => Math.random() - 0.5).join('');
}

export const EmployeeDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirmation();
  const { user: currentUser } = useAuth();
  const isSystemUser = !currentUser?.is_employee;
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docForm, setDocForm] = useState({ name: '', type: 'other', notes: '' });
  const [completion, setCompletion] = useState({});
  const [credentials, setCredentials] = useState(null);
  const [credLoading, setCredLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locLoading, setLocLoading] = useState(false);

  // Activation dialog state
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [salaryCertRefresh, setSalaryCertRefresh] = useState(0);
  const [roles, setRoles] = useState([]);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [activateForm, setActivateForm] = useState({ role_id: '', password: '', send_email: false });
  const [showActivatePw, setShowActivatePw] = useState(false);
  const [activating, setActivating] = useState(false);

  // Reset password dialog
  const [showResetPw, setShowResetPw] = useState(false);
  const [resetPwValue, setResetPwValue] = useState('');
  const [showResetPwVisible, setShowResetPwVisible] = useState(false);
  const [resetSendEmail, setResetSendEmail] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', designation: '', department: '',
    joining_date: '', salary: '', joining_ctc: '', status: 'active', work_days: [0,1,2,3,4], overtime_rate: '0', standard_hours: '8',
    basic_percent: '50', hra_percent: '25',
    attendance_exempt: false,
    date_of_birth: '', gender: '', address: '', city: '', state: '', pincode: '', emergency_contact_name: '', emergency_contact_phone: '',
    bank_name: '', account_number: '', ifsc_code: '', branch_name: '', account_holder_name: '',
    pf_number: '', uan_number: '', esi_number: '', pan_number: '',
    official_email: '', official_mobile: '',
    work_start_time: '09:00', work_end_time: '18:00',
  });

  const fetchEmployee = useCallback(async () => {
    try {
      const res = await api.get(`/employees/${id}`);
      if (res.data.success) {
        const e = res.data.data;
        setEmployee(e);
        setForm(prev => ({ ...prev, ...e, salary: String(e.salary || ''), joining_ctc: String(e.joining_ctc || ''), overtime_rate: String(e.overtime_rate || '0'), standard_hours: String(e.standard_hours || '8'), work_days: e.work_days || [0,1,2,3,4], basic_percent: String(e.basic_percent || '50'), hra_percent: String(e.hra_percent || '25'), work_start_time: e.work_start_time || '09:00', work_end_time: e.work_end_time || '18:00', attendance_exempt: !!e.attendance_exempt }));
      }
    } catch { toast.error('Failed to load employee'); }
    finally { setLoading(false); }
  }, [id]);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await api.get(`/employee-docs/${id}`);
      if (res.data.success) setDocuments(res.data.data);
    } catch {}
  }, [id]);

  const fetchCompletion = useCallback(async () => {
    try {
      const res = await api.get(`/employees/${id}/completion`);
      if (res.data.success) setCompletion(res.data.data);
    } catch {}
  }, [id]);

  const fetchCredentials = useCallback(async () => {
    setCredLoading(true);
    try {
      const res = await api.get(`/employees/${id}/credentials`);
      if (res.data.success) setCredentials(res.data.data);
    } catch {}
    finally { setCredLoading(false); }
  }, [id]);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await api.get('/roles');
      if (res.data.success) setRoles(res.data.data);
    } catch {}
  }, []);

  const fetchDesignations = useCallback(async () => {
    try {
      const res = await api.get('/designations');
      if (res.data?.success) {
        setDesignationOptions(
          (res.data.data || []).map(d => ({
            value: d.name, label: d.name, department: d.department || '',
          }))
        );
      }
    } catch {}
  }, []);

  const fetchLocations = useCallback(async () => {
    setLocLoading(true);
    try {
      const res = await api.get(`/auth/locations/by-employee/${id}`);
      if (res.data.success) setLocations(res.data.data);
    } catch {}
    finally { setLocLoading(false); }
  }, [id]);

  useEffect(() => { fetchEmployee(); fetchDocuments(); fetchCompletion(); fetchRoles(); fetchDesignations(); }, [fetchEmployee, fetchDocuments, fetchCompletion, fetchRoles, fetchDesignations]);

  const handleSave = async () => {
    // Warn if changing official_email on an active account (it propagates to login email)
    const currentEmail = (employee?.official_email || '').trim().toLowerCase();
    const newEmail = (form.official_email || '').trim().toLowerCase();
    if (
      employee?.account_status === 'active' &&
      newEmail && currentEmail && newEmail !== currentEmail
    ) {
      const ok = await confirm({
        title: 'Change login email?',
        description: `This will update the login email from "${currentEmail}" to "${newEmail}". The employee will need to use the new email to log in. Continue?`,
        confirmText: 'Yes, change login email',
        cancelText: 'Cancel',
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      const payload = { ...form, salary: parseFloat(form.salary) || 0, joining_ctc: (parseFloat(form.joining_ctc) > 0 ? parseFloat(form.joining_ctc) : (parseFloat(form.salary) || 0)), overtime_rate: parseFloat(form.overtime_rate) || 0, standard_hours: parseFloat(form.standard_hours) || 8 };
      await api.put(`/employees/${id}`, payload);
      await api.put(`/attendance/employee-settings/${id}`, { work_days: payload.work_days, overtime_rate: payload.overtime_rate, standard_hours: payload.standard_hours });
      toast.success('Employee updated');
      fetchEmployee();
      fetchCompletion();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!docForm.name) { toast.error('Enter document name first'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'employee_document');
      const uploadRes = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (uploadRes.data.success) {
        await api.post(`/employee-docs/${id}`, {
          name: docForm.name, type: docForm.type, notes: docForm.notes,
          file_id: uploadRes.data.data.id, file_name: uploadRes.data.data.original_name, file_size: uploadRes.data.data.size,
        });
        toast.success('Document uploaded');
        setDocForm({ name: '', type: 'other', notes: '' });
        fetchDocuments();
      }
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDeleteDoc = async (docId) => {
    const confirmed = await confirm({ title: 'Delete Document', description: 'Are you sure you want to delete this document? This action cannot be undone.' });
    if (!confirmed) return;
    try {
      await api.delete(`/employee-docs/${id}/${docId}`);
      toast.success('Document removed');
      fetchDocuments();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleWorkDay = (day) => setForm(prev => ({ ...prev, work_days: prev.work_days.includes(day) ? prev.work_days.filter(d => d !== day) : [...prev.work_days, day].sort() }));

  // Activation handler
  const handleActivate = async () => {
    if (!activateForm.role_id) { toast.error('Please select a role'); return; }
    if (!checkPasswordStrength(activateForm.password).valid) { toast.error(PASSWORD_POLICY_MESSAGE); return; }
    setActivating(true);
    try {
      const res = await api.post(`/employees/${id}/activate`, activateForm);
      if (res.data.success) {
        toast.success(res.data.message);
        setShowActivateDialog(false);
        setActivateForm({ role_id: '', password: '', send_email: false });
        fetchEmployee();
        fetchCompletion();
        fetchCredentials();
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Activation failed'); }
    finally { setActivating(false); }
  };

  // Deactivate/Reactivate login
  const handleToggleLogin = async (action) => {
    const endpoint = action === 'deactivate' ? 'deactivate-login' : 'reactivate-login';
    const label = action === 'deactivate' ? 'Deactivate' : 'Reactivate';
    const confirmed = await confirm({
      title: `${label} Login`,
      description: `Are you sure you want to ${action} the login for this employee?`,
      confirmText: label,
      variant: action === 'deactivate' ? 'destructive' : 'default',
    });
    if (!confirmed) return;
    try {
      await api.post(`/employees/${id}/${endpoint}`);
      toast.success(`Login ${action}d successfully`);
      fetchEmployee();
      fetchCredentials();
    } catch (err) { toast.error(err.response?.data?.detail || 'Operation failed'); }
  };

  // Reset credentials handler
  const handleResetCredentials = async (e) => {
    e.preventDefault();
    if (!checkPasswordStrength(resetPwValue).valid) { toast.error(PASSWORD_POLICY_MESSAGE); return; }
    setResetLoading(true);
    try {
      await api.post(`/employees/${id}/reset-credentials`, { password: resetPwValue, send_email: resetSendEmail });
      toast.success('Password reset successfully');
      setShowResetPw(false);
      setResetPwValue('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Reset failed'); }
    finally { setResetLoading(false); }
  };

  const uploadEmployeePicture = async (blob) => {
    const fd = new FormData();
    fd.append('file', blob, 'profile.jpg');
    const res = await api.post('/employees/upload-profile-picture', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    if (res.data.success) {
      const picPath = res.data.data.path;
      setForm(f => ({ ...f, profile_picture: picPath }));
      // Persist to the employee record (also syncs to linked user via the
      // CRUD-factory profile_picture sync).
      await api.put(`/employees/${id}`, { profile_picture: picPath });
      toast.success('Profile picture updated');
    }
  };

  const deleteEmployeePicture = async () => {
    await api.put(`/employees/${id}`, { profile_picture: '' });
    setForm(f => ({ ...f, profile_picture: '' }));
    toast.success('Profile picture removed');
  };

  // Permission-based gate. The earlier `!is_employee` check was fragile —
  // it broke for HR admins who happened to ALSO have an employee record
  // (e.g. anyone who went through the App-Live email reset). Source of
  // truth is now "can this user manage other employees".
  const canViewLogins = !!(currentUser?.permissions || []).find(
    p => p === '*' || p === 'employees.edit' || p === 'employees.view_all'
         || p === 'attendance.view_all' || p === 'hr.manage'
  );

  // Same RBAC as the Users module — only privileged users can re-assign a
  // login's role from here. Mirrors the gate used on the User edit page.
  const canEditRole = !!(currentUser?.permissions || []).find(
    p => p === '*' || p === 'users.edit' || p === 'users.manage' || p === 'roles.manage'
  );

  const [roleSaving, setRoleSaving] = useState(false);

  const handleChangeRole = async (newRoleId) => {
    if (!credentials?.user_id || !newRoleId) return;
    if (newRoleId === credentials.role?.id) return;
    setRoleSaving(true);
    try {
      await api.put(`/users/${credentials.user_id}`, { role_id: newRoleId });
      toast.success('Role updated. The employee will see the new permissions on next page load.');
      await fetchCredentials();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update role');
    } finally {
      setRoleSaving(false);
    }
  };

  const tabs = [
    { id: 'personal', label: 'Personal', icon: User },
    { id: 'job', label: 'Job', icon: Briefcase },
    { id: 'salary', label: 'Salary', icon: IndianRupee },
    { id: 'bank', label: 'Bank', icon: Building2 },
    { id: 'documents', label: 'Documents', icon: FileUp },
    { id: 'credentials', label: 'Credentials', icon: KeyRound },
    ...(canViewLogins ? [{ id: 'locations', label: 'Logins', icon: MapPin }] : []),
  ];

  useEffect(() => {
    if (activeTab === 'credentials') fetchCredentials();
    if (activeTab === 'locations' && canViewLogins) fetchLocations();
  }, [activeTab, fetchCredentials, fetchLocations, canViewLogins]);

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const accountStatus = employee?.account_status || 'inactive';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employee-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/employees')} data-testid="back-to-employees"><ArrowLeft className="h-5 w-5" /></Button>
          <div className="flex items-center gap-4">
            {isSystemUser ? (
              <ProfilePictureUploader
                picture={form.profile_picture}
                firstName={form.first_name}
                lastName={form.last_name}
                seed={id}
                size="lg"
                onUpload={uploadEmployeePicture}
                onDelete={deleteEmployeePicture}
              />
            ) : (
              <PersonAvatar firstName={form.first_name} lastName={form.last_name} picture={form.profile_picture} seed={id} size="lg" />
            )}
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold">{form.first_name} {form.last_name}</h1>
                <Badge className={accountStatus === 'active' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-gray-500/10 text-gray-400'}>
                  {accountStatus === 'active' ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">{form.designation} | {form.department}</p>
            </div>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-employee-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      {/* Tabs with completion indicators */}
      <div className="flex gap-1 border-b border-border overflow-x-auto" data-testid="employee-tabs">
        {tabs.map(tab => {
          const isComplete = completion[tab.id];
          const isRequired = ['personal', 'job', 'salary', 'bank'].includes(tab.id);
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {isRequired && tab.id !== 'credentials' && (
                isComplete
                  ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  : <Circle className="h-3.5 w-3.5 text-muted-foreground/40" />
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'personal' && (
        <Card data-testid="tab-personal-content">
          <CardHeader><CardTitle className="text-lg">Personal Details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>First Name *</Label><Input value={form.first_name} onChange={e => setForm(f => ({...f, first_name: e.target.value}))} data-testid="emp-first-name" /></div>
              <div className="space-y-2"><Label>Last Name *</Label><Input value={form.last_name} onChange={e => setForm(f => ({...f, last_name: e.target.value}))} data-testid="emp-last-name" /></div>
              <div className="space-y-2">
                <Label>Personal Email *</Label>
                <Input
                  type="email"
                  value={form.personal_email || form.email || ''}
                  onChange={e => setForm(f => ({...f, personal_email: e.target.value}))}
                  data-testid="emp-personal-email"
                />
                {(() => {
                  const personal = (form.personal_email || form.email || '').trim().toLowerCase();
                  const official = (form.official_email || '').trim().toLowerCase();
                  if (personal && official && personal === official) {
                    return (
                      <p className="text-xs text-amber-400 flex items-start gap-1.5" data-testid="emp-email-same-warning">
                        <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        Personal and Official emails are the same — set them differently if you want HR comms to go to a personal address.
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>
              <div className="space-y-2"><Label>Phone *</Label><Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} data-testid="emp-phone" /></div>
              <div className="space-y-2"><Label>Date of Birth</Label><Input type="date" value={form.date_of_birth || ''} onChange={e => setForm(f => ({...f, date_of_birth: e.target.value}))} data-testid="emp-dob" /></div>
              <div className="space-y-2"><Label>Gender</Label>
                <Select value={form.gender || 'not_specified'} onValueChange={v => setForm(f => ({...f, gender: v}))}>
                  <SelectTrigger data-testid="emp-gender"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="not_specified">Not specified</SelectItem><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2"><Label>Address</Label><Input value={form.address || ''} onChange={e => setForm(f => ({...f, address: e.target.value}))} placeholder="Full address" data-testid="emp-address" /></div>
              <div className="space-y-2"><Label>City</Label><Input value={form.city || ''} onChange={e => setForm(f => ({...f, city: e.target.value}))} data-testid="emp-city" /></div>
              <div className="space-y-2"><Label>State</Label><Input value={form.state || ''} onChange={e => setForm(f => ({...f, state: e.target.value}))} /></div>
              <div className="space-y-2"><Label>Pincode</Label><Input value={form.pincode || ''} onChange={e => setForm(f => ({...f, pincode: e.target.value}))} /></div>
            </div>
            <div className="border-t border-border pt-4 space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase">Emergency Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Name</Label><Input value={form.emergency_contact_name || ''} onChange={e => setForm(f => ({...f, emergency_contact_name: e.target.value}))} data-testid="emp-emergency-name" /></div>
                <div className="space-y-2"><Label>Phone</Label><Input value={form.emergency_contact_phone || ''} onChange={e => setForm(f => ({...f, emergency_contact_phone: e.target.value}))} data-testid="emp-emergency-phone" /></div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'job' && (
        <Card data-testid="tab-job-content">
          <CardHeader><CardTitle className="text-lg">Job Details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label>Official Email *</Label>
                <Input
                  type="email"
                  value={form.official_email || ''}
                  onChange={e => setForm(f => ({...f, official_email: e.target.value}))}
                  placeholder="employee@company.com"
                  data-testid="emp-official-email"
                />
                {employee?.account_status === 'active' ? (
                  <p className="text-xs text-amber-400 flex items-start gap-1.5">
                    <Mail className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    This is also the user's <strong>login email</strong>. Changing it here updates the login.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">Used as the login email when this employee is activated.</p>
                )}
              </div>
              <div className="space-y-2"><Label>Official Mobile</Label><Input value={form.official_mobile || ''} onChange={e => setForm(f => ({...f, official_mobile: e.target.value}))} placeholder="+91 XXXXX XXXXX" data-testid="emp-official-mobile" /></div>
              <div className="space-y-2">
                <Label>Designation *</Label>
                <Select
                  value={form.designation || ''}
                  onValueChange={v => {
                    const match = designationOptions.find(o => o.value === v);
                    setForm(f => ({ ...f, designation: v, department: match?.department || '' }));
                  }}
                >
                  <SelectTrigger data-testid="emp-designation"><SelectValue placeholder="Select designation" /></SelectTrigger>
                  <SelectContent>
                    {designationOptions.map(o => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}{o.department ? <span className="text-xs text-muted-foreground ml-2">· {o.department}</span> : null}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!designationOptions.some(o => o.value === form.designation) && form.designation ? (
                  <p className="text-xs text-amber-500" data-testid="emp-designation-legacy">
                    Legacy title "{form.designation}" is not in the master list — please re-pick.
                  </p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Input
                  value={form.department || ''}
                  readOnly
                  tabIndex={-1}
                  data-testid="emp-department"
                  placeholder="Auto-filled from designation"
                  className="bg-muted/40 cursor-not-allowed"
                />
                <p className="text-xs text-muted-foreground">Derived from the selected designation.</p>
              </div>
              <div className="space-y-2"><Label>Joining Date *</Label><Input type="date" value={form.joining_date ? form.joining_date.split('T')[0] : ''} onChange={e => setForm(f => ({...f, joining_date: e.target.value}))} data-testid="emp-joining" /></div>
              <div className="space-y-2"><Label>Employment Status</Label>
                <Select value={form.status} onValueChange={v => setForm(f => ({...f, status: v}))}>
                  <SelectTrigger data-testid="emp-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="on_leave">On Leave</SelectItem><SelectItem value="terminated">Terminated</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="border-t border-border pt-4 space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase">Work Schedule</h3>
              <div className="space-y-2">
                <Label>Working Days</Label>
                <div className="flex gap-2" data-testid="emp-work-days">
                  {DAY_LABELS.map((d, i) => (
                    <button key={i} type="button" onClick={() => toggleWorkDay(i)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${form.work_days.includes(i) ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                      data-testid={`emp-wd-${i}`}>{d}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Standard Hours/Day</Label><Input type="number" step="0.5" value={form.standard_hours} onChange={e => setForm(f => ({...f, standard_hours: e.target.value}))} data-testid="emp-std-hours" /></div>
                <div className="space-y-2"><Label>Overtime Rate (per day multiplier)</Label><Input type="number" step="0.5" min="0" value={form.overtime_rate} onChange={e => setForm(f => ({...f, overtime_rate: e.target.value}))} data-testid="emp-ot-rate" />
                  {(() => {
                    const sal = (parseFloat(form.salary) || 0) / 12;
                    const rate = parseFloat(form.overtime_rate) || 0;
                    const wdPerWeek = form.work_days.length || 5;
                    if (sal > 0 && rate > 0) {
                      const weekendDays = Math.floor((30 / 7) * (7 - wdPerWeek));
                      const workingDays = 30 - weekendDays;
                      const perDay = sal / workingDays;
                      const otAmount = Math.ceil((perDay * rate) / 50) * 50;
                      return <p className="text-xs text-muted-foreground mt-1">Approx. per day: <span className="font-semibold text-emerald-500">&#8377;{otAmount.toLocaleString('en-IN')}</span> ({workingDays} working days/month)</p>;
                    }
                    return null;
                  })()}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Office Start Time</Label><Input type="time" value={form.work_start_time || '09:00'} onChange={e => setForm(f => ({...f, work_start_time: e.target.value}))} data-testid="emp-work-start-time" /></div>
                <div className="space-y-2"><Label>Office End Time</Label><Input type="time" value={form.work_end_time || '18:00'} onChange={e => setForm(f => ({...f, work_end_time: e.target.value}))} data-testid="emp-work-end-time" /></div>
              </div>
              <div className="rounded-md border border-border bg-muted/30 p-4 flex items-start justify-between gap-4" data-testid="emp-attendance-exempt-row">
                <div className="space-y-1">
                  <Label htmlFor="emp-attendance-exempt" className="text-sm font-semibold">Exempt from attendance-based deduction</Label>
                  <p className="text-xs text-muted-foreground max-w-xl">
                    When enabled, payroll is calculated assuming the employee is present on every working day. No salary is deducted for missing attendance, and overtime is suppressed. Use for directors and senior staff who don&apos;t punch in daily.
                    {form.attendance_exempt && (
                      <span className="block mt-1 text-amber-500">Note: explicit unpaid-leave entries still deduct that day.</span>
                    )}
                  </p>
                </div>
                <Switch
                  id="emp-attendance-exempt"
                  checked={!!form.attendance_exempt}
                  onCheckedChange={(v) => setForm(f => ({...f, attendance_exempt: !!v}))}
                  data-testid="emp-attendance-exempt-toggle"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'salary' && (
        <Card data-testid="tab-salary-content">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg">Salary Details</CardTitle>
            <SalaryCertificateDialog
              employeeId={id}
              employeeName={`${form.first_name || ''} ${form.last_name || ''}`.trim()}
              annualCtc={form.salary}
              employeeEmail={form.official_email || form.email}
              disabled={!id}
              onSuccess={() => setSalaryCertRefresh(t => t + 1)}
            />
          </CardHeader>
          <CardContent className="space-y-4">
            <SalaryCertificateHistory
              employeeId={id}
              refreshToken={salaryCertRefresh}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Annual CTC (INR) *</Label><Input type="number" value={form.salary} onChange={e => setForm(f => ({...f, salary: e.target.value}))} data-testid="emp-salary" /></div>
              <div className="space-y-2">
                <Label>Joining CTC (INR)</Label>
                <Input
                  type="number"
                  value={form.joining_ctc || ''}
                  onChange={e => setForm(f => ({...f, joining_ctc: e.target.value}))}
                  placeholder={form.salary ? `Auto-fills to ${form.salary} if blank` : 'Optional'}
                  data-testid="emp-joining-ctc"
                />
                <p className="text-xs text-muted-foreground">CTC at the time of joining. Stays anchored as appraisals raise the current CTC.</p>
              </div>
              <div className="space-y-2">
                <Label>Basic %</Label>
                <Select value={form.basic_percent} onValueChange={v => setForm(f => ({...f, basic_percent: v}))}>
                  <SelectTrigger data-testid="emp-basic-pct"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="40">40%</SelectItem><SelectItem value="45">45%</SelectItem><SelectItem value="50">50%</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>HRA %</Label>
                <Select value={form.hra_percent} onValueChange={v => setForm(f => ({...f, hra_percent: v}))}>
                  <SelectTrigger data-testid="emp-hra-pct"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="20">20%</SelectItem><SelectItem value="25">25%</SelectItem><SelectItem value="30">30%</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>PAN Number</Label><Input value={form.pan_number || ''} onChange={e => setForm(f => ({...f, pan_number: e.target.value}))} placeholder="ABCDE1234F" data-testid="emp-pan" /></div>
              <div className="space-y-2"><Label>PF Number</Label><Input value={form.pf_number || ''} onChange={e => setForm(f => ({...f, pf_number: e.target.value}))} data-testid="emp-pf" /></div>
              <div className="space-y-2"><Label>UAN Number</Label><Input value={form.uan_number || ''} onChange={e => setForm(f => ({...f, uan_number: e.target.value}))} data-testid="emp-uan" /></div>
              <div className="space-y-2"><Label>ESI Number</Label><Input value={form.esi_number || ''} onChange={e => setForm(f => ({...f, esi_number: e.target.value}))} data-testid="emp-esi" /></div>
            </div>
            {(() => {
              const current = parseFloat(form.salary) || 0;
              const joining = parseFloat(form.joining_ctc) || 0;
              if (current <= 0 || joining <= 0 || joining === current) return null;
              const diff = current - joining;
              const pct = ((diff / joining) * 100).toFixed(1);
              const isUp = diff > 0;
              return (
                <div className={`p-3 rounded-lg text-sm flex items-center justify-between ${isUp ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-500'}`} data-testid="emp-ctc-growth">
                  <span>CTC growth since joining</span>
                  <span className="font-semibold tabular-nums">
                    {isUp ? '+' : ''}{formatCurrency(diff)} ({isUp ? '+' : ''}{pct}%)
                  </span>
                </div>
              );
            })()}
            {(() => {
              const ctc = parseFloat(form.salary) || 0;
              if (ctc <= 0) return null;
              const basicPct = parseFloat(form.basic_percent) || 50;
              const hraPct = parseFloat(form.hra_percent) || 25;
              const monthly = Math.round(ctc / 12);
              const basicM = Math.round((ctc * basicPct / 100) / 12);
              const hraM = Math.round((ctc * hraPct / 100) / 12);
              const convM = 1600;
              const medM = 1250;
              const ptM = 200;
              const specialM = monthly - basicM - hraM - convM - medM;
              const effectiveM = monthly - ptM;
              return (
                <div className="border-t border-border pt-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-3">Monthly Salary Breakdown</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div className="p-3 rounded-lg bg-muted/30"><p className="text-muted-foreground">Basic ({basicPct}%)</p><p className="text-lg font-bold">{formatCurrency(basicM)}</p></div>
                    <div className="p-3 rounded-lg bg-muted/30"><p className="text-muted-foreground">HRA ({hraPct}%)</p><p className="text-lg font-bold">{formatCurrency(hraM)}</p></div>
                    <div className="p-3 rounded-lg bg-muted/30"><p className="text-muted-foreground">Conveyance</p><p className="text-lg font-bold">{formatCurrency(convM)}</p></div>
                    <div className="p-3 rounded-lg bg-muted/30"><p className="text-muted-foreground">Medical</p><p className="text-lg font-bold">{formatCurrency(medM)}</p></div>
                    <div className="p-3 rounded-lg bg-muted/30"><p className="text-muted-foreground">Special Allowance</p><p className="text-lg font-bold">{formatCurrency(Math.max(specialM, 0))}</p></div>
                    <div className="p-3 rounded-lg bg-red-500/10"><p className="text-red-400">Prof. Tax (Yearly: {formatCurrency(2400)})</p><p className="text-lg font-bold text-red-400">-{formatCurrency(ptM)}</p></div>
                    <div className="p-3 rounded-lg bg-primary/10 col-span-2"><p className="text-primary">Effective Monthly Salary</p><p className="text-2xl font-bold text-primary">{formatCurrency(effectiveM)}</p></div>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {activeTab === 'bank' && (
        <Card data-testid="tab-bank-content">
          <CardHeader><CardTitle className="text-lg">Bank Details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Bank Name *</Label><Input value={form.bank_name || ''} onChange={e => setForm(f => ({...f, bank_name: e.target.value}))} placeholder="e.g. State Bank of India" data-testid="emp-bank-name" /></div>
              <div className="space-y-2"><Label>Account Holder Name *</Label><Input value={form.account_holder_name || ''} onChange={e => setForm(f => ({...f, account_holder_name: e.target.value}))} data-testid="emp-holder-name" /></div>
              <div className="space-y-2"><Label>Account Number *</Label><Input value={form.account_number || ''} onChange={e => setForm(f => ({...f, account_number: e.target.value}))} data-testid="emp-account-no" /></div>
              <div className="space-y-2"><Label>IFSC Code *</Label><Input value={form.ifsc_code || ''} onChange={e => setForm(f => ({...f, ifsc_code: e.target.value}))} placeholder="e.g. SBIN0001234" data-testid="emp-ifsc" /></div>
              <div className="space-y-2"><Label>Branch Name</Label><Input value={form.branch_name || ''} onChange={e => setForm(f => ({...f, branch_name: e.target.value}))} data-testid="emp-branch" /></div>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'documents' && (
        <div className="space-y-4" data-testid="tab-documents-content">
          <Card>
            <CardHeader><CardTitle className="text-lg">Upload Document</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div className="space-y-2"><Label>Document Name *</Label><Input value={docForm.name} onChange={e => setDocForm(f => ({...f, name: e.target.value}))} placeholder="e.g. Aadhaar Card" data-testid="doc-name" /></div>
                <div className="space-y-2"><Label>Type</Label>
                  <Select value={docForm.type} onValueChange={v => setDocForm(f => ({...f, type: v}))}>
                    <SelectTrigger data-testid="doc-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{docTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Notes</Label><Input value={docForm.notes} onChange={e => setDocForm(f => ({...f, notes: e.target.value}))} placeholder="Optional" /></div>
                <div>
                  <Label htmlFor="doc-upload" className={`flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors ${uploading ? 'bg-muted text-muted-foreground' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}>
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {uploading ? 'Uploading...' : 'Upload File'}
                  </Label>
                  <input id="doc-upload" type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} data-testid="doc-file-input" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-lg">Uploaded Documents ({documents.length})</CardTitle></CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground"><FileUp className="h-10 w-10 mx-auto mb-3 opacity-40" /><p>No documents uploaded yet</p></div>
              ) : (
                <div className="space-y-2">
                  {documents.map(doc => (
                    <div key={doc.id} className="flex items-center gap-4 p-3 rounded-lg bg-muted/20 border border-border" data-testid={`doc-row-${doc.id}`}>
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><FileUp className="h-5 w-5 text-primary" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{doc.name}</p>
                        <p className="text-xs text-muted-foreground">{doc.file_name} | {(doc.file_size / 1024).toFixed(1)} KB | <Badge className="text-[10px] bg-primary/10 text-primary">{doc.type}</Badge></p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="icon" asChild data-testid={`doc-download-${doc.id}`}>
                          <a href={`${API_URL}/api/uploads/${doc.file_id}`} target="_blank" rel="noopener noreferrer"><Download className="h-4 w-4" /></a>
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteDoc(doc.id)} className="text-destructive hover:text-destructive" data-testid={`doc-delete-${doc.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'credentials' && (
        <div className="space-y-4" data-testid="tab-credentials-content">
          {credLoading ? (
            <div className="flex items-center justify-center h-48"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : credentials?.has_login ? (
            <>
              {/* Active login display */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ShieldCheck className="h-5 w-5 text-emerald-500" />
                      Login Account
                    </CardTitle>
                    <Badge className={credentials.account_status === 'active' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}>
                      {credentials.account_status === 'active' ? 'Active' : 'Deactivated'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-muted/20 border border-border">
                      <p className="text-xs text-muted-foreground mb-1">Login Email (Username)</p>
                      <p className="font-medium" data-testid="cred-email">{credentials.official_email}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/20 border border-border">
                      <p className="text-xs text-muted-foreground mb-2">Assigned Role</p>
                      {canEditRole ? (
                        <Select
                          value={credentials.role?.id || ''}
                          onValueChange={handleChangeRole}
                          disabled={roleSaving}
                        >
                          <SelectTrigger className="h-9" data-testid="cred-role-select">
                            <SelectValue placeholder="Select a role…" />
                          </SelectTrigger>
                          <SelectContent>
                            {(roles || []).map(r => (
                              <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="font-medium" data-testid="cred-role">{credentials.role?.name || 'N/A'}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => { setResetPwValue(''); setShowResetPw(true); }} data-testid="cred-reset-pw-btn">
                      <KeyRound className="h-4 w-4 mr-2" />Reset Password
                    </Button>
                    {credentials.account_status === 'active' ? (
                      <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => handleToggleLogin('deactivate')} data-testid="cred-deactivate-btn">
                        <UserX className="h-4 w-4 mr-2" />Deactivate Login
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" className="text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/10" onClick={() => handleToggleLogin('reactivate')} data-testid="cred-reactivate-btn">
                        <UserCheck className="h-4 w-4 mr-2" />Reactivate Login
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {/* No login - show activation card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ShieldAlert className="h-5 w-5 text-amber-500" />
                    No Login Account
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    This employee does not have a login account yet. Complete all required sections to activate.
                  </p>

                  {/* Completion checklist */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Activation Requirements:</p>
                    {[
                      { key: 'personal', label: 'Personal Details' },
                      { key: 'job', label: 'Job Details (incl. Official Email)' },
                      { key: 'salary', label: 'Salary Details' },
                      { key: 'bank', label: 'Bank Details' },
                    ].map(item => (
                      <div key={item.key} className="flex items-center gap-2 text-sm">
                        {completion[item.key]
                          ? <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          : <Circle className="h-4 w-4 text-muted-foreground/40" />
                        }
                        <span className={completion[item.key] ? 'text-foreground' : 'text-muted-foreground'}>{item.label}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    disabled={!completion.can_activate}
                    onClick={() => setShowActivateDialog(true)}
                    className="mt-2"
                    data-testid="activate-employee-btn"
                  >
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    {completion.can_activate ? 'Activate Employee' : 'Complete Required Sections First'}
                  </Button>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Locations Tab - System Users Only */}
      {activeTab === 'locations' && canViewLogins && (
        <div className="space-y-4" data-testid="tab-locations-content">
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="text-lg flex items-center gap-2"><MapPin className="h-5 w-5 text-rose-500" />Login History</CardTitle>
              {(currentUser?.permissions || []).includes('*') && (
                <Button variant="outline" size="sm" onClick={async () => {
                  try {
                    const r = await api.post('/auth/locations/backfill-geocode');
                    toast.success(r.data?.message || 'Backfill complete');
                    fetchLocations();
                  } catch (e) { toast.error(e.response?.data?.detail || 'Backfill failed'); }
                }} data-testid="locations-backfill-btn">Backfill Addresses</Button>
              )}
            </CardHeader>
            <CardContent>
              {locLoading ? (
                <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : locations.length === 0 ? (
                <p className="text-muted-foreground text-sm py-8 text-center">No logins captured yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date & Time</TableHead>
                      <TableHead>Accuracy</TableHead>
                      <TableHead>IP Address</TableHead>
                      <TableHead>Coordinates</TableHead>
                      <TableHead>Location</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {locations.map(loc => {
                      const hasCoords = loc.latitude != null && loc.longitude != null;
                      return (
                      <TableRow key={loc.id}>
                        <TableCell className="text-sm">{loc.captured_at ? new Date(loc.captured_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', hour12: true }) : '-'}</TableCell>
                        <TableCell>
                          {hasCoords ? (
                            <>
                              <Badge className={loc.accuracy_type === 'high' ? 'bg-emerald-500/10 text-emerald-500' : loc.accuracy_type === 'low' ? 'bg-amber-500/10 text-amber-500' : 'bg-gray-500/10 text-gray-400'}>
                                {loc.accuracy_type === 'high' ? 'High' : loc.accuracy_type === 'low' ? 'Low' : 'Unknown'}
                              </Badge>
                              {loc.accuracy_meters != null && <span className="text-xs text-muted-foreground ml-1.5">({Math.round(loc.accuracy_meters)}m)</span>}
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground italic">No geo</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs" data-testid={`loc-ip-${loc.id}`}>{loc.ip_address || <span className="text-muted-foreground">-</span>}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {hasCoords ? (
                            <a href={`https://www.google.com/maps/search/?api=1&query=${loc.latitude},${loc.longitude}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid={`loc-coords-link-${loc.id}`}>
                              {Number(loc.latitude).toFixed(5)}, {Number(loc.longitude).toFixed(5)}
                            </a>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {(() => {
                            const addr = loc.formatted_address || [loc.area, loc.city, loc.state, loc.country].filter(Boolean).join(', ');
                            if (!addr) return <span className="text-muted-foreground">-</span>;
                            if (!hasCoords) return <span className="text-sm">{addr}</span>;
                            return (
                              <a href={`https://www.google.com/maps/search/?api=1&query=${loc.latitude},${loc.longitude}`} target="_blank" rel="noopener noreferrer" className="hover:text-primary hover:underline transition-colors" data-testid={`loc-address-link-${loc.id}`}>
                                {addr}
                              </a>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Activation Dialog */}
      <Dialog open={showActivateDialog} onOpenChange={setShowActivateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-500" />Activate Employee</DialogTitle>
            <DialogDescription>Create a login account for {form.first_name} {form.last_name} using their official email: <strong>{form.official_email}</strong></DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Assign Role *</Label>
              <Select value={activateForm.role_id} onValueChange={v => setActivateForm(f => ({...f, role_id: v}))}>
                <SelectTrigger data-testid="activate-role-select"><SelectValue placeholder="Select a role" /></SelectTrigger>
                <SelectContent>{roles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Initial Password *</Label>
                <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1" onClick={() => { const pw = generateStrongPassword(); setActivateForm(f => ({...f, password: pw})); setShowActivatePw(true); }} data-testid="activate-generate-pw">
                  <Wand2 className="h-3 w-3" />Auto Generate
                </Button>
              </div>
              <div className="relative">
                <Input type={showActivatePw ? 'text' : 'password'} value={activateForm.password} onChange={e => setActivateForm(f => ({...f, password: e.target.value}))} className="pr-10" data-testid="activate-password-input" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowActivatePw(!showActivatePw)} tabIndex={-1}>
                  {showActivatePw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <PasswordStrengthHint password={activateForm.password} />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="activate-send-email" checked={activateForm.send_email} onCheckedChange={v => setActivateForm(f => ({...f, send_email: v}))} data-testid="activate-send-email" />
              <label htmlFor="activate-send-email" className="text-sm text-muted-foreground flex items-center gap-1.5 cursor-pointer">
                <Mail className="h-3.5 w-3.5" />Send credentials via email
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivateDialog(false)}>Cancel</Button>
            <Button onClick={handleActivate} disabled={activating} data-testid="activate-confirm-btn">
              {activating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              Activate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPw} onOpenChange={setShowResetPw}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" />Reset Password</DialogTitle>
            <DialogDescription>Set a new password for {form.first_name} {form.last_name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetCredentials} className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>New Password *</Label>
                <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1" onClick={() => { const pw = generateStrongPassword(); setResetPwValue(pw); setShowResetPwVisible(true); }} data-testid="cred-generate-pw">
                  <Wand2 className="h-3 w-3" />Auto Generate
                </Button>
              </div>
              <div className="relative">
                <Input type={showResetPwVisible ? 'text' : 'password'} value={resetPwValue} onChange={e => setResetPwValue(e.target.value)} required minLength={8} className="pr-10" data-testid="cred-reset-pw-input" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowResetPwVisible(!showResetPwVisible)} tabIndex={-1}>
                  {showResetPwVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <PasswordStrengthHint password={resetPwValue} />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="cred-send-email" checked={resetSendEmail} onCheckedChange={setResetSendEmail} data-testid="cred-send-email" />
              <label htmlFor="cred-send-email" className="text-sm text-muted-foreground flex items-center gap-1.5 cursor-pointer">
                <Mail className="h-3.5 w-3.5" />Send new password via email
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowResetPw(false)}>Cancel</Button>
              <Button type="submit" disabled={resetLoading} data-testid="cred-reset-pw-submit">{resetLoading ? 'Resetting...' : 'Reset Password'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
